import { io } from 'socket.io-client';
import { refreshAccessToken } from '../../../Config/ApiConfig';
import { SocketCustomService } from './socketCustomService';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.connecting = false;
    this.connectionListeners = [];
    this.errorListeners = [];
    this.eventListeners = {};
    this.resourceCounters = {};
    this.resourceIdCounters = {};
    this.config = {
      serverUrl: '',
      accessToken: '',
    };
    this.isRefreshingToken = false;
    this.reconnectQueue = [];
    this.lastEventDateTime = null;
  }

  // Connection methods
  initialize(serverUrl, accessToken) {
    this.config.serverUrl = serverUrl;
    this.config.accessToken = accessToken;
    console.log('Socket service initialized');
  }

  connect() {
    if (!this.config.serverUrl) return;

    // If already connecting, don't attempt to connect again
    if (this.connecting) {
      console.log('Already connecting to socket server, connection attempt skipped');
      return;
    }

    this.connecting = true;
    this._notifyConnectionListeners({ status: 'connecting' });

    console.log('Connecting to Socket.IO server:', this.config.serverUrl);

    this.socket = io(this.config.serverUrl, {
      auth: { access_token: this.config.accessToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      path: '/socket.io',
      transports: ['websocket'],
      upgrade: false,
      forceNew: true,
      secure: true,
      rejectUnauthorized: false,
      withCredentials: true
    });

    this._setupSocketListeners();
    console.log('Socket connecting...');
  }

  disconnect() {
    if (!this.socket) return;

    this.connected = false;
    this.connecting = false;
    this.socket.disconnect();
    this._notifyConnectionListeners({ status: 'disconnected' });
    this.socket = null;
    this.lastEventDateTime = null;
    this.resourceCounters = {};
    this.eventListeners = {};
    this.resourceIdCounters = {};
    this.reconnectQueue = [];
    this.isRefreshingToken = false;
    this.connectionListeners = [];
    this.errorListeners = [];
    console.log('Socket disconnected');
  }

  updateToken(accessToken) {
    this.config.accessToken = accessToken;

    if (this.socket && this.connected) {
      // For the next reconnection
      this.socket.auth = { access_token: accessToken };
    }
  }

  // Token refresh handling
  async _handleAuthError() {
    // If already refreshing, add to queue
    if (this.isRefreshingToken) {
      return new Promise((resolve, reject) => {
        this.reconnectQueue.push({ resolve, reject });
      });
    }

    this.isRefreshingToken = true;

    try {
      console.log('Attempting to refresh token for socket connection');
      const newToken = await refreshAccessToken();

      // Update the socket token
      this.updateToken(newToken.replace('Bearer ', ''));

      // Process queue
      this.reconnectQueue.forEach(promise => promise.resolve(newToken));
      this.reconnectQueue = [];

      // Reconnect with new token
      this.disconnect();
      this.connect();

      return newToken;
    } catch (error) {
      console.error('Failed to refresh token for socket connection:', error);

      // Reject all pending promises
      this.reconnectQueue.forEach(promise => promise.reject(error));
      this.reconnectQueue = [];
      throw error;
    } finally {
      this.isRefreshingToken = false;
    }
  }

  // Event listener methods
  onConnect(listener) {
    this.connectionListeners.push(listener);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(l => l !== listener);
    };
  }

  onError(listener) {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== listener);
    };
  }

  listenToEvent(resource, action, callback) {
    console.log('[SocketService] listenToEvent called:', {
      resource,
      action,
      callback
    });

    const eventName = `${resource}:${action}`;

    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];

      if (this.socket) {
        this.socket.on(eventName, (data) => {
          console.log('handleEvent', data);
          this._handleEvent(eventName, data);
        });
      }
    }

    this.eventListeners[eventName].push(callback);
    console.log(`Registered listener for ${eventName}`);

    return () => this.stopListeningToEvent(resource, action, callback);
  }

  stopListeningToEvent(resource, action, callback) {
    const eventName = `${resource}:${action}`;

    if (!this.eventListeners[eventName]) return;

    this.eventListeners[eventName] = this.eventListeners[eventName].filter(cb => cb !== callback);

    if (this.eventListeners[eventName].length === 0) {
      delete this.eventListeners[eventName];

      if (this.socket) {
        this.socket.off(eventName);
      }
    }

    console.log(`Removed listener for ${eventName}`);
  }

  // Resource subscription methods
  subscribeToResource(resource, resourceIds = null) {
    console.log('[SocketService] subscribeToResource called:', {
      resource,
      resourceIds,
      connected: this.connected
    });

    if (!this.connected) {
      console.warn('Cannot subscribe when socket is not connected');
      return;
    }

    if (!resourceIds) {
      // Subscribe to the entire resource
      this.resourceCounters[resource] = (this.resourceCounters[resource] || 0) + 1;

      if (this.resourceCounters[resource] === 1) {
        // First subscription to this resource
        this._emitSubscription(resource);
      }
    } else {
      // Subscribe to specific resource IDs
      if (!this.resourceIdCounters[resource]) {
        this.resourceIdCounters[resource] = {};
        console.log('[SocketService] Initialized resource ID counters for:', resource);
      }

      const idsToSubscribe = [];

      console.log('[SocketService] Processing resource IDs:', {
        resource,
        incomingIds: resourceIds,
        currentCounters: this.resourceIdCounters[resource]
      });

      resourceIds.forEach(id => {
        this.resourceIdCounters[resource][id] = (this.resourceIdCounters[resource][id] || 0) + 1;

        console.log('[SocketService] Updated counter for ID:', {
          resource,
          id,
          newCount: this.resourceIdCounters[resource][id]
        });

        if (this.resourceIdCounters[resource][id] === 1) {
          idsToSubscribe.push(id);
          console.log('[SocketService] Added ID to subscription list:', {
            resource,
            id,
            totalToSubscribe: idsToSubscribe.length
          });
        }
      });

      if (idsToSubscribe.length > 0) {
        console.log('[SocketService] Emitting subscription for new IDs:', {
          resource,
          idsToSubscribe,
          totalCounters: Object.keys(this.resourceIdCounters[resource]).length
        });
        this._emitSubscription(resource, idsToSubscribe);
      } else {
        console.log('[SocketService] No new IDs to subscribe:', {
          resource,
          reason: 'all IDs already subscribed'
        });
      }
    }
  }

  unsubscribeFromResource(resource, resourceIds = null) {
    if (!this.connected) return;

    if (!resourceIds) {
      // Unsubscribe from the entire resource
      if (!this.resourceCounters[resource]) return;

      this.resourceCounters[resource] -= 1;

      if (this.resourceCounters[resource] <= 0) {
        delete this.resourceCounters[resource];
        this._emitUnsubscription(resource);
      }
    } else {
      // Unsubscribe from specific resource IDs
      if (!this.resourceIdCounters[resource]) return;

      const idsToUnsubscribe = [];

      resourceIds.forEach(id => {
        if (!this.resourceIdCounters[resource][id]) return;

        this.resourceIdCounters[resource][id] -= 1;

        if (this.resourceIdCounters[resource][id] <= 0) {
          delete this.resourceIdCounters[resource][id];
          idsToUnsubscribe.push(id);
        }
      });

      if (idsToUnsubscribe.length > 0) {
        this._emitUnsubscription(resource, idsToUnsubscribe);
      }

      // Clean up if no more subscriptions for this resource
      if (Object.keys(this.resourceIdCounters[resource]).length === 0) {
        delete this.resourceIdCounters[resource];
      }
    }
  }

  // Private methods
  _setupSocketListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connected = true;
      this.connecting = false;
      console.log('Socket connected, notifying listeners');
      this._notifyConnectionListeners({ status: 'connected' });

      // First resubscribe to all channels
      this._resubscribeAll();

      // Then set up all registered event listeners after resubscription
      this._setupEventListeners();

      console.log('Socket connected successfully');
    });

    this.socket.on('disconnect', (reason) => {
      this._notifyConnectionListeners({ status: 'disconnected' });

      if (this.connected) {
        this._handleAuthError()
          .then(() => {
            console.log('Token refreshed after disconnect, reconnecting socket');
          })
          .catch(err => {
            console.error('Failed to refresh token after disconnect:', err);
          });
      }
    });

    this.socket.on('error', (error) => {
      this._notifyErrorListeners({
        type: 'server_error',
        ...error
      });
      console.error('Socket server error:', error);
    });
  }

  _setupEventListeners() {
    if (!this.socket || !this.connected) return;

    // Set up all registered event listeners
    Object.keys(this.eventListeners).forEach(eventName => {
      // Remove any existing listeners for this event to prevent duplicates
      this.socket.off(eventName);

      // Register the event listener
      this.socket.on(eventName, (data) => {
        this._handleEvent(eventName, data);
      });
    });

    console.log('Re-established event listeners for', Object.keys(this.eventListeners).length, 'events');
  }

  _handleEvent(eventName, data) {
    if (!this.eventListeners[eventName]) return;

    // Update the last event timestamp if the data has created_at
    console.log('Received event:', eventName, data);
    if (data && data.created_at) {
      this.lastEventDateTime = data.created_at;
      console.log('Updated last event timestamp to:', this.lastEventDateTime);
    }

    this.eventListeners[eventName].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for ${eventName}:`, error);
      }
    });
  }

  _emitSubscription(resource, resourceIds = null) {
    if (!this.socket || !this.connected) return;

    const subscriptionData = {
      resource: resource,
      resource_ids: resourceIds,
    };

    this.socket.emit('subscribe', subscriptionData);

    console.log(`Emitted subscription for ${resource}${resourceIds ? `:${resourceIds.join(',')}` : ''}`);
  }

  _emitUnsubscription(resource, resourceIds = null) {
    if (!this.socket || !this.connected) return;

    const unsubscriptionData = {
      resource: resource,
      resource_ids: resourceIds
    };

    this.socket.emit('unsubscribe', unsubscriptionData);
    console.log(`Emitted unsubscription for ${resource}${resourceIds ? `:${resourceIds.join(',')}` : ''}`);
  }

  _resubscribeAll() {
    // Don't attempt to resubscribe if we're not connected
    if (!this.socket || !this.connected) {
      console.log('Cannot resubscribe when socket is not connected');
      return;
    }

    // Add from_datetime to all resubscriptions
    const fromDateTime = this.lastEventDateTime;
    console.log(`Resubscribing from ${fromDateTime || 'beginning'}`);
    // Resubscribe to all resources
    Object.keys(this.resourceCounters).forEach(resource => {
      const subscriptionData = {
        resource: resource,
        from_datetime: fromDateTime
      };
      this.socket.emit('subscribe', subscriptionData);
      console.log(`Resubscribed to ${resource} from ${fromDateTime || 'beginning'}`);
    });

    // Resubscribe to all resource IDs
    Object.keys(this.resourceIdCounters).forEach(resource => {
      const ids = Object.keys(this.resourceIdCounters[resource]);
      if (ids.length > 0) {
        const subscriptionData = {
          resource: resource,
          resource_ids: ids,
          from_datetime: fromDateTime
        };
        this.socket.emit('subscribe', subscriptionData);
        console.log(`Resubscribed to ${resource}:${ids.join(',')} from ${fromDateTime || 'beginning'}`);
      }
    });
  }

  _notifyConnectionListeners(state) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  _notifyErrorListeners(error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

}

// Create a singleton instance
const socketService = new SocketService();

// Make the socket service globally available for disconnection on logout
window.socketService = socketService;

export default socketService;
